import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
import { calcularComparativo } from "../utils/comparativeFunctions";
import { ComparativeInputData, ComparativeResultsType, ValidationMessages, CONSTANTES_2025 } from "../calculadora-comparativa";

const CALCULATOR_KEY = "calculadora-comparativa";
const MAX_DEPENDENTES = 20;
const MIN_DEPENDENTES = 0;

const DEFAULT_VALUES: ComparativeInputData = {
  salarioBruto: "",
  numeroDependentes: "0",
  dependentesMenores14: "0",
  valeAlimentacao: "",
  valeTransporte: "",
  planoSaude: "",
  outrosBeneficios: "",
  plr: "", 
  regimePJ: 'mei',
} as const;

const Tooltip = ({ children, text }: { children: React.ReactNode, text: string }) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 max-w-xs text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-normal z-20 text-center">
      {text}
    </div>
  </div>
);

const parseNumericValue = (value: string): number => {
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0;
};

const parseIntegerValue = (value: string): number => {
  return Math.max(0, Number.parseInt(value) || 0);
};

export const ComparativeInputCard = ({
  setResults,
}: {
  setResults: (value: ComparativeResultsType | null) => void;
}) => {
  const [message, setMessage] = useState<string>("");
  const [formData, setFormData] = useState<ComparativeInputData>(DEFAULT_VALUES);
  const [isCalculating, setIsCalculating] = useState(false);

  const isVerified = useIsVerified();

  const updateFormField = <K extends keyof ComparativeInputData>(field: K, value: ComparativeInputData[K]) => {
    setFormData((prev: ComparativeInputData) => ({ ...prev, [field]: value }));
  };

  const validateSalario = (salario: string): string | null => {
    if (!salario) return ValidationMessages.SALARIO_OBRIGATORIO;
    
    const salarioNum = parseNumericValue(salario);
    if (salarioNum <= 0) return ValidationMessages.SALARIO_INVALIDO;
    
    if (salarioNum < CONSTANTES_2025.SALARIO_MINIMO) {
      return `Salário deve ser pelo menos R$ ${CONSTANTES_2025.SALARIO_MINIMO.toFixed(2).replace('.', ',')} (salário mínimo)`;
    }
    
    return null;
  };

  const validateDependentes = (dependentes: string, dependentesMenores: string): string | null => {
    const dependentesNum = parseIntegerValue(dependentes);
    const dependentesMenoresNum = parseIntegerValue(dependentesMenores);
    
    if (dependentesMenoresNum > dependentesNum) {
      return ValidationMessages.DEPENDENTES_INVALIDOS;
    }
    
    return null;
  };

  const validateForm = (): string | null => {
    return validateSalario(formData.salarioBruto) || 
           validateDependentes(formData.numeroDependentes, formData.dependentesMenores14);
  };

  const executeCalculation = async () => {
    const resultado = calcularComparativo(
      formData.salarioBruto,
      formData.numeroDependentes,
      formData.dependentesMenores14,
      formData.valeAlimentacao,
      formData.valeTransporte,
      formData.planoSaude,
      formData.outrosBeneficios,
      formData.plr,
      formData.regimePJ
    );

    setResults(resultado);
    setMessage("");
  };

  const calcular = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setIsCalculating(true);
    setMessage("");

    try {
      await executeCalculation();
    } catch (error) {
      console.error('Erro no cálculo:', error);
      setMessage((error as Error).message || ValidationMessages.ERRO_CALCULO);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const clear = () => {
    setFormData(DEFAULT_VALUES);
    setResults(null);
    setMessage("");
  };

  const handleSave = () => {
    saveCalculatorData(CALCULATOR_KEY, {
      salarioBruto: formData.salarioBruto,
      numeroDependentes: formData.numeroDependentes,
      dependentesMenores14: formData.dependentesMenores14,
      valeAlimentacao: formData.valeAlimentacao,
      valeTransporte: formData.valeTransporte,
      planoSaude: formData.planoSaude,
      outrosBeneficios: formData.outrosBeneficios,
      plr: formData.plr,
      regimePJ: formData.regimePJ,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
    checkCalculatorUsageLimitAndRedirect(calcular);
  };

  useEffect(() => {
    const savedData = loadSavedCalculatorData(CALCULATOR_KEY);
    if (savedData) {
      setFormData({
        salarioBruto: (savedData.salarioBruto as string) || DEFAULT_VALUES.salarioBruto,
        numeroDependentes: (savedData.numeroDependentes as string) || DEFAULT_VALUES.numeroDependentes,
        dependentesMenores14: (savedData.dependentesMenores14 as string) || DEFAULT_VALUES.dependentesMenores14,
        valeAlimentacao: (savedData.valeAlimentacao as string) || DEFAULT_VALUES.valeAlimentacao,
        valeTransporte: (savedData.valeTransporte as string) || DEFAULT_VALUES.valeTransporte,
        planoSaude: (savedData.planoSaude as string) || DEFAULT_VALUES.planoSaude,
        outrosBeneficios: (savedData.outrosBeneficios as string) || DEFAULT_VALUES.outrosBeneficios,
        plr: (savedData.plr as string) || DEFAULT_VALUES.plr,
        regimePJ: (savedData.regimePJ as 'mei' | 'simples' | 'presumido') || DEFAULT_VALUES.regimePJ,
      });
    }
  }, []);

  return (
    <MainCard>
      <MainCardTitle>Calculadora Comparativa CLT x PJ</MainCardTitle>
      <form onSubmit={handleSubmit}>
        <MainCardContent>
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="salario-bruto">
              Salário Bruto CLT *
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="salario-bruto"
                placeholder="0,00"
                type="text"
                value={formData.salarioBruto}
                onChange={(event) => updateFormField('salarioBruto', sanitizeBrlValue(event))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium text-sm" htmlFor="numero-dependentes">
                Número de Dependentes
              </label>
              <input
                className="w-full p-2 border rounded"
                id="numero-dependentes"
                placeholder="0"
                type="number"
                min={MIN_DEPENDENTES}
                max={MAX_DEPENDENTES}
                value={formData.numeroDependentes}
                onChange={(event) => updateFormField('numeroDependentes', event.target.value)}
              />
            </div>
            <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="dependentes-menores-14">
                  Nº de menores de 14 anos:
                </label>
              <input
                className="w-full p-2 border rounded"
                id="dependentes-menores-14"
                placeholder="0"
                type="number"
                min={MIN_DEPENDENTES}
                max={MAX_DEPENDENTES}
                value={formData.dependentesMenores14}
                onChange={(event) => updateFormField('dependentesMenores14', event.target.value)}
              />
            </div>
          </div>

          <div className="bg-[#e9ecef] p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Benefícios CLT (opcional)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="vale-alimentacao">
                  Vale Alimentação
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="vale-alimentacao"
                    placeholder="0,00"
                    type="text"
                    value={formData.valeAlimentacao}
                    onChange={(event) => updateFormField('valeAlimentacao', sanitizeBrlValue(event))}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="vale-transporte">
                  Vale Transporte
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="vale-transporte"
                    placeholder="0,00"
                    type="text"
                    value={formData.valeTransporte}
                    onChange={(event) => updateFormField('valeTransporte', sanitizeBrlValue(event))}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="plano-saude">
                  Plano de Saúde
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="plano-saude"
                    placeholder="0,00"
                    type="text"
                    value={formData.planoSaude}
                    onChange={(event) => updateFormField('planoSaude', sanitizeBrlValue(event))}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="outros-beneficios">
                  Outros Benefícios
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="outros-beneficios"
                    placeholder="0,00"
                    type="text"
                    value={formData.outrosBeneficios}
                    onChange={(event) => updateFormField('outrosBeneficios', sanitizeBrlValue(event))}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-white rounded border border-gray-300">
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <label className="font-medium text-sm" htmlFor="plr">
                    PLR/Participação nos Lucros
                  </label>
                  <Tooltip text="PLR é isenta até R$ 7.640,80. Acima disso, tabela progressiva de 7,5% a 27,5% (tributação exclusiva na fonte).">
                    <span className="text-blue-600 cursor-help text-sm">ℹ️</span>
                  </Tooltip>
                </div>
                <div className="flex border border-gray-300 rounded">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef] border-gray-300">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="plr"
                    placeholder="0,00"
                    type="text"
                    value={formData.plr}
                    onChange={(event) => updateFormField('plr', sanitizeBrlValue(event))}
                  />
                </div>
              </div>
              
              <div>
              </div>
            </div>
          </div>

          <div className="bg-[#e9ecef] p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Regime Tributário para Comparação
            </h3>
            
            <div className="grid grid-cols-1 gap-3">
              <label className="flex items-start gap-3 p-3 bg-white rounded border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="regimePJ"
                  value="mei"
                  checked={formData.regimePJ === 'mei'}
                  onChange={(e) => updateFormField('regimePJ', e.target.value as 'mei')}
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{accentColor: '#8d0202'}}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">MEI (Microempreendedor Individual)</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <div>• Limite: R$ 81.000/ano (R$ 6.750/mês)</div>
                    <div>• Impostos: R$ 76,90 a R$ 81,90/mês</div>
                  </div>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 bg-white rounded border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="regimePJ"
                  value="simples"
                  checked={formData.regimePJ === 'simples'}
                  onChange={(e) => updateFormField('regimePJ', e.target.value as 'simples')}
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{accentColor: '#8d0202'}}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">Simples Nacional</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <div>• Limite: R$ 4.800.000/ano</div>
                    <div>• Impostos: IRPJ, CSLL, PIS/COFINS + ICMS/ISS</div>
                  </div>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 bg-white rounded border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="regimePJ"
                  value="presumido"
                  checked={formData.regimePJ === 'presumido'}
                  onChange={(e) => updateFormField('regimePJ', e.target.value as 'presumido')}
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{accentColor: '#8d0202'}}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">Lucro Presumido</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <div>• Sem limite de faturamento</div>
                    <div>• Impostos: 11,33% a 16,33% conforme atividade</div>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </MainCardContent>

        <div className="flex justify-between">
          <Button
            disabled={!isVerified || isCalculating}
            type="submit"
            variant="default"
            className="sm:w-[150px]"
          >
            {isCalculating ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Comparando...
              </>
            ) : (
              "Comparar"
            )}
          </Button>
          <Button onClick={clear} variant="ghost" type="button">
            Limpar
          </Button>
        </div>
      </form>
      
      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};